import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, doc, onSnapshot, orderBy, query, where, or } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { 
  gamesData,
  contactInfo 
} from '../data/mockData';
import { auth, db } from '../firebase';
import { addMessageFs, updateMessageFs, updateGamificationFs } from '../utils/firestoreStore';
import { isCurrentSeasonContent } from '../utils/seasonContent';

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const { user } = useAuth();
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [data, setData] = useState({
    assignments: [],
    submissions: [],
    marks: [],
    resources: [],
    announcements: [],
    calendarEvents: [],
    studySessions: [],
    messages: [],
    users: [],
    gamificationStats: null,
    gamificationLeaderboard: [],
    feeStructure: null,
    ads: [],
    courses: [],
    communityMessages: [],
    attendanceRecords: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useFirebase) {
      const hasFirebaseSession = Boolean(auth.currentUser?.uid) && auth.currentUser?.uid === user?.id;
      if (!user || !hasFirebaseSession) {
        setData({
          assignments: [],
          submissions: [],
          marks: [],
          resources: [],
          announcements: [],
          calendarEvents: [],
          studySessions: [],
          messages: [],
          users: [],
          gamificationStats: null,
          gamificationLeaderboard: [],
          feeStructure: null,
          courses: [],
          communityMessages: [],
          attendanceRecords: []
        });
        setLoading(false);
        return undefined;
      }

      setLoading(true);
      let readyCount = 0;
      const userRoles = user?.roles || (user?.role ? [user.role] : []);
      const isAdmin = userRoles.includes('admin');
      const isTeacher = userRoles.includes('teacher');
      const requiredReady = 14;
      const markReady = () => {
        readyCount += 1;
        if (readyCount >= requiredReady) {
          setLoading(false);
        }
      };

      const assignmentsRef = query(collection(db, 'assignments'), orderBy('createdAt', 'desc'));
      const resourcesRef = query(collection(db, 'resources'), orderBy('createdAt', 'desc'));
      const announcementsRef = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
      const submissionsRef = isAdmin
        ? query(collection(db, 'submissions'), orderBy('createdAt', 'desc'))
        : isTeacher
          ? query(
              collection(db, 'submissions'),
              where('teacherId', '==', user.id),
              orderBy('createdAt', 'desc')
            )
          : query(
            collection(db, 'submissions'),
            where('studentId', '==', user.id),
            orderBy('createdAt', 'desc')
          );
      const marksRef = isAdmin
        ? query(collection(db, 'marks'), orderBy('createdAt', 'desc'))
        : query(
            collection(db, 'marks'),
            where(isTeacher ? 'teacherId' : 'studentId', '==', user.id),
            orderBy('createdAt', 'desc')
          );
      const adsRef = query(collection(db, 'ads'), orderBy('createdAt', 'desc'));
      const messagesRef = isAdmin
        ? query(collection(db, 'messages'), orderBy('createdAt', 'desc'))
        : query(collection(db, 'messages'), or(where('senderId', '==', user.id), where('recipientId', '==', user.id)), orderBy('createdAt', 'desc'));
      const calendarRef = query(collection(db, 'calendar'), orderBy('createdAt', 'desc'));
      const coursesRef = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
      const feesRef = doc(db, 'settings', 'fees');
      const gamificationRef = query(collection(db, 'gamification'), orderBy('points', 'desc'));
      const communityRef = query(collection(db, 'communityMessages'), orderBy('createdAt', 'desc'));
      const attendanceRef = isAdmin || isTeacher
        ? query(collection(db, 'attendanceRecords'), orderBy('createdAt', 'desc'))
        : query(collection(db, 'attendanceRecords'), where('userId', '==', user.id));

      const unsubAssignments = onSnapshot(
        assignmentsRef,
        (snapshot) => {
          const next = snapshot.docs
            .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
            .filter(isCurrentSeasonContent);
          setData(prev => ({ ...prev, assignments: next }));
          markReady();
        },
        (error) => {
          console.error('Assignments listener failed', error);
          setData(prev => ({ ...prev, assignments: [] }));
          markReady();
        }
      );

      const unsubResources = onSnapshot(
        resourcesRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => {
            const data = docSnap.data() || {};
            const { id: _ignored, ...rest } = data;
            return { id: docSnap.id, ...rest };
          });
          setData(prev => ({ ...prev, resources: next }));
          markReady();
        },
        (error) => {
          console.error('Resources listener failed', error);
          setData(prev => ({ ...prev, resources: [] }));
          markReady();
        }
      );

      const unsubAnnouncements = onSnapshot(
        announcementsRef,
        (snapshot) => {
          const next = snapshot.docs
            .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
            .filter(isCurrentSeasonContent);
          setData(prev => ({ ...prev, announcements: next }));
          markReady();
        },
        (error) => {
          console.error('Announcements listener failed', error);
          setData(prev => ({ ...prev, announcements: [] }));
          markReady();
        }
      );

      let unsubFees = () => {};
      if (isAdmin) {
        unsubFees = onSnapshot(
          feesRef,
          (snapshot) => {
            const next = snapshot.exists() ? snapshot.data() : null;
            setData(prev => ({ ...prev, feeStructure: next }));
            markReady();
          },
          (error) => {
            console.error('Fees listener failed', error);
            setData(prev => ({ ...prev, feeStructure: null }));
            markReady();
          }
        );
      } else {
        setData(prev => ({ ...prev, feeStructure: null }));
        markReady();
      }

      const unsubSubmissions = onSnapshot(
        submissionsRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          setData(prev => ({ ...prev, submissions: next }));
          markReady();
        },
        (error) => {
          console.error('Submissions listener failed', error);
          setData(prev => ({ ...prev, submissions: [] }));
          markReady();
        }
      );

      const unsubMarks = onSnapshot(
        marksRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          setData(prev => ({ ...prev, marks: next }));
          markReady();
        },
        (error) => {
          console.error('Marks listener failed', error);
          setData(prev => ({ ...prev, marks: [] }));
          markReady();
        }
      );

      const unsubAds = onSnapshot(
        adsRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => {
            const data = docSnap.data() || {};
            const { id: _ignored, ...rest } = data;
            return { id: docSnap.id, ...rest };
          });
          setData(prev => ({ ...prev, ads: next }));
          markReady();
        },
        (error) => {
          console.error('Ads listener failed', error);
          setData(prev => ({ ...prev, ads: [] }));
          markReady();
        }
      );

      const unsubMessages = onSnapshot(
        messagesRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          setData(prev => ({ ...prev, messages: next }));
          markReady();
        },
        (error) => {
          console.error('Messages listener failed', error);
          setData(prev => ({ ...prev, messages: [] }));
          markReady();
        }
      );

      const unsubCalendar = onSnapshot(
        calendarRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          setData(prev => ({ ...prev, calendarEvents: next }));
          markReady();
        },
        (error) => {
          console.error('Calendar listener failed', error);
          setData(prev => ({ ...prev, calendarEvents: [] }));
          markReady();
        }
      );

      let unsubUsers = () => {};
      if (isAdmin) {
        const usersRef = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        unsubUsers = onSnapshot(
          usersRef,
          (snapshot) => {
            const next = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
            setData(prev => ({ ...prev, users: next }));
            markReady();
          },
          (error) => {
            console.error('Users listener failed', error);
            setData(prev => ({ ...prev, users: [] }));
            markReady();
          }
        );
      } else {
        const currentUserRef = doc(db, 'users', user.id);
        unsubUsers = onSnapshot(
          currentUserRef,
          (snapshot) => {
            const next = snapshot.exists() ? [{ id: snapshot.id, ...snapshot.data() }] : [];
            setData(prev => ({ ...prev, users: next }));
            markReady();
          },
          (error) => {
            console.error('Users listener failed', error);
            setData(prev => ({ ...prev, users: [] }));
            markReady();
          }
        );
      }

      const unsubCourses = onSnapshot(
        coursesRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          setData(prev => ({ ...prev, courses: next }));
          markReady();
        },
        (error) => {
          console.error('Courses listener failed', error);
          setData(prev => ({ ...prev, courses: [] }));
          markReady();
        }
      );

      const unsubGamification = onSnapshot(
        gamificationRef,
        (snapshot) => {
          const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
          const leaderboard = rows.map((row, index) => ({
            id: row.id,
            userId: row.userId || row.id,
            name: row.name || 'Student',
            points: row.points || 0,
            badges: Array.isArray(row.badges) ? row.badges.length : 0,
            rank: index + 1,
            isCurrent: (row.userId || row.id) === user?.id
          }));
          const current = rows.find(row => (row.userId || row.id) === user?.id) || null;
          setData(prev => ({
            ...prev,
            gamificationStats: current,
            gamificationLeaderboard: leaderboard
          }));
          markReady();
        },
        (error) => {
          console.error('Gamification listener failed', error);
          setData(prev => ({ ...prev, gamificationLeaderboard: [] }));
          markReady();
        }
      );

      const unsubCommunity = onSnapshot(
        communityRef,
        (snapshot) => {
          const next = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          setData(prev => ({ ...prev, communityMessages: next }));
          markReady();
        },
        (error) => {
          console.error('Community listener failed', error);
          setData(prev => ({ ...prev, communityMessages: [] }));
          markReady();
        }
      );

      const unsubAttendance = onSnapshot(
        attendanceRef,
        (snapshot) => {
          const next = snapshot.docs
            .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
            .sort((left, right) => {
              const getTime = (value) => {
                if (value?.toDate) return value.toDate().getTime();
                if (typeof value === 'string' || typeof value === 'number') {
                  const parsed = new Date(value).getTime();
                  return Number.isNaN(parsed) ? 0 : parsed;
                }
                return 0;
              };
              return getTime(right.createdAt) - getTime(left.createdAt);
            });
          setData(prev => ({ ...prev, attendanceRecords: next }));
          markReady();
        },
        (error) => {
          console.error('Attendance listener failed', error);
          setData(prev => ({ ...prev, attendanceRecords: [] }));
          markReady();
        }
      );

      return () => {
        unsubAssignments();
        unsubResources();
        unsubAnnouncements();
        unsubFees();
        unsubSubmissions();
        unsubMarks();
        unsubAds();
        unsubMessages();
        unsubCalendar();
        unsubUsers();
        unsubCourses();
        unsubGamification();
        unsubCommunity();
        unsubAttendance();
      };
    }

    setData({
      assignments: [],
      submissions: [],
      marks: [],
      resources: [],
      announcements: [],
      calendarEvents: [],
      studySessions: [],
      messages: [],
      users: [],
      gamificationStats: null,
      gamificationLeaderboard: [],
      feeStructure: null,
      ads: [],
      courses: [],
      communityMessages: [],
      attendanceRecords: []
    });
    setLoading(false);
    return undefined;
  }, [user, useFirebase]);

  const addSubmission = () => {};
  const addStudySession = () => {};
  const addMessage = async (payload) => {
    if (!useFirebase) return null;
    return addMessageFs(payload);
  };
  const updateMessage = async (messageId, updates) => {
    if (!useFirebase) return null;
    return updateMessageFs(messageId, updates);
  };
  const saveGamification = async ({ userId, name, pointsDelta, badge }) => {
    if (!useFirebase || !userId) return null;
    return updateGamificationFs(userId, { name, pointsDelta, badge });
  };
  const updateMarks = () => {};
  const updateGamification = () => {};

  const value = {
    ...data,
    loading,
    feeStructure: data.feeStructure || null,
    contactInfo,
    gamesData,
    gamificationStats: data.gamificationStats || null,
    courses: data.courses || [],
    users: data.users || [],
    addSubmission,
    addStudySession,
    addMessage,
    updateMessage,
    saveGamification,
    updateMarks,
    updateGamification,
    reloadData: async () => {}
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
